import React, { useState, useRef, useEffect } from 'react';

type Option = {
  label: string;
  value: string;
  disabled?: boolean;
};

interface CustomSelectProps {
  options: Option[];
  value?: string | number | undefined;
  onChange?: (value?: string) => void;
  placeholder?: string;
  searchable?: boolean;
}

export default function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  searchable = true,
}: CustomSelectProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find((opt) => opt.value === value);

  const handleOptionSelect = (option: Option) => {
    onChange?.(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev === 0 ? filteredOptions.length - 1 : prev - 1
        );
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (filteredOptions[highlightedIndex])
          handleOptionSelect(filteredOptions[highlightedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    const selectedIndex = filteredOptions.findIndex(
      (opt) => opt.value === value
    );

    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const el = itemRefs.current[highlightedIndex];
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, highlightedIndex]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className='position-relative'
    >
      <div
        className='form-select d-flex align-items-center justify-content-between position-relative'
        role='button'
        tabIndex={0}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
      >
        <span className='text-truncate w-100'>
          {selectedOption?.label || (
            <span className='text-muted'>{placeholder}</span>
          )}
        </span>
      </div>

      {isOpen && (
        <div className='dropdown-menu show w-100 mt-1 p-0'>
          {searchable && (
            <div className='p-2 border-bottom'>
              <input
                ref={inputRef}
                type='text'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='form-control form-control-sm'
                placeholder='Buscar...'
              />
            </div>
          )}
          {/* eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex */}
          <ul
            className='list-unstyled mb-0 overflow-auto'
            style={{ maxHeight: '200px' }}
            role='listbox'
            aria-activedescendant={`option-${highlightedIndex}`}
          >
            {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
            <li
              className='dropdown-item text-muted'
              aria-disabled='true'
              tabIndex={-1}
              key='unselectable'
            >
              {placeholder}
            </li>

            {filteredOptions.length === 0 ? (
              // eslint-disable-next-line jsx-a11y/role-supports-aria-props
              <li
                className='dropdown-item text-muted'
                aria-disabled='true'
                key='undefined_no_options'
              >
                Nenhuma opção encontrada
              </li>
            ) : (
              filteredOptions.map((option, index) => (
                <li
                  ref={(el) => {
                    itemRefs.current[index] = el;
                    return el;
                  }}
                  key={`key-${option.value}`}
                  id={`option-${index}`}
                  role='option'
                  aria-selected={option.value === value}
                  tabIndex={0}
                  className={`dropdown-item cursor-pointer ${
                    index === highlightedIndex ? 'active' : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOptionSelect(option)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOptionSelect(option);
                    }
                  }}
                >
                  {option.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
